import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { TrendingUp, Download, Trophy, Target } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api, { mensajeDeError, pesos, STORAGE } from '../lib/api';
import { toast } from 'sonner';

/**
 * Métricas de la plataforma y exportación para contabilidad.
 *
 * Todo es agregado: no hay ninguna vista que permita seguir a una persona.
 */
export const AdminMetricas = () => {
  const [metricas, setMetricas] = useState(null);
  const [dias, setDias] = useState(30);
  const [cargando, setCargando] = useState(true);

  const hoy = format(new Date(), 'yyyy-MM-dd');
  const [desde, setDesde] = useState(format(new Date(), 'yyyy-MM-01'));
  const [hasta, setHasta] = useState(hoy);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/admin/metricas', { params: { days: dias } });
      setMetricas(data);
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos cargar las métricas'));
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /**
   * La descarga se arma a mano con fetch porque necesita el token en la
   * cabecera: un enlace normal no puede enviarlo.
   */
  const exportar = async () => {
    try {
      const respuesta = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/exportar/reservas?from=${desde}&to=${hasta}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem(STORAGE.adminToken)}` } }
      );

      if (!respuesta.ok) {
        throw new Error('El servidor rechazó la descarga');
      }

      const blob = await respuesta.blob();
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');

      enlace.href = url;
      enlace.download = `reservas_${desde}_${hasta}.csv`;
      document.body.appendChild(enlace);
      enlace.click();
      document.body.removeChild(enlace);
      URL.revokeObjectURL(url);

      toast.success('Descarga lista');
    } catch (error) {
      toast.error(error.message || 'No pudimos generar la descarga');
    }
  };

  const serie = metricas?.series ?? [];
  const maxReservas = Math.max(1, ...serie.map((d) => d.bookings));

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Cómo viene la plataforma
              </CardTitle>
              <CardDescription className="text-slate-400">
                Últimos {dias} días · {metricas?.totals?.bookings ?? 0} reservas ·{' '}
                {pesos(metricas?.totals?.revenue ?? 0)}
              </CardDescription>
            </div>

            <div className="flex gap-2">
              {[7, 30, 90].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={dias === d ? 'default' : 'outline'}
                  onClick={() => setDias(d)}
                  className={
                    dias === d
                      ? 'bg-teal-600 hover:bg-teal-700 text-white'
                      : 'border-slate-600 text-slate-300 hover:bg-slate-700'
                  }
                >
                  {d} días
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {cargando && <p className="text-slate-400 py-6">Cargando…</p>}

          {!cargando && metricas?.totals?.bookings === 0 && (
            <p className="text-slate-400 py-8 text-center">
              Todavía no hay reservas en este período.
            </p>
          )}

          {!cargando && (metricas?.totals?.bookings ?? 0) > 0 && (
            <div className="overflow-x-auto">
              <div className="flex items-end gap-1 h-40 min-w-[400px]">
                {serie.map((dia) => (
                  <div key={dia.date} className="flex-1 flex flex-col justify-end group relative">
                    <div
                      className={`rounded-t transition-colors ${
                        dia.bookings > 0 ? 'bg-teal-500 group-hover:bg-teal-400' : 'bg-slate-700'
                      }`}
                      style={{ height: `${Math.max(2, (dia.bookings / maxReservas) * 100)}%` }}
                    />
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      {format(parseISO(`${dia.date}T00:00:00`), 'd MMM', { locale: es })}:{' '}
                      {dia.bookings} · {pesos(dia.revenue)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-slate-400 mt-2 min-w-[400px]">
                <span>
                  {serie[0] && format(parseISO(`${serie[0].date}T00:00:00`), 'd MMM', { locale: es })}
                </span>
                <span>
                  {serie.length > 0 &&
                    format(parseISO(`${serie[serie.length - 1].date}T00:00:00`), 'd MMM', { locale: es })}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Trophy className="w-5 h-5" />
              Profesionales con más sesiones
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(metricas?.top_psychologists?.length ?? 0) === 0 ? (
              <p className="text-slate-400 py-4">Sin datos en este período.</p>
            ) : (
              <div className="space-y-2">
                {metricas.top_psychologists.map((p, i) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-900/60"
                  >
                    <span className="text-slate-200 truncate">
                      <span className="text-slate-400 mr-2">{i + 1}.</span>
                      {p.name}
                    </span>
                    <span className="text-sm text-slate-400 flex-none ml-3">
                      {p.sessions} · {pesos(p.revenue)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Target className="w-5 h-5" />
              Qué se está pidiendo más
            </CardTitle>
            <CardDescription className="text-slate-400">
              Útil para saber a quién conviene reclutar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(metricas?.specialties_demand?.length ?? 0) === 0 ? (
              <p className="text-slate-400 py-4">Sin datos en este período.</p>
            ) : (
              <div className="space-y-2">
                {metricas.specialties_demand.map((e) => (
                  <div key={e.specialty} className="flex items-center gap-3">
                    <span className="text-slate-300 text-sm w-40 truncate">{e.specialty}</span>
                    <div className="flex-1 h-2 rounded-full bg-slate-900 overflow-hidden">
                      <div
                        className="h-full bg-teal-500"
                        style={{
                          width: `${(e.sessions / metricas.specialties_demand[0].sessions) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-slate-400 text-sm w-8 text-right">{e.sessions}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Download className="w-5 h-5" />
            Exportar para contabilidad
          </CardTitle>
          <CardDescription className="text-slate-400">
            Planilla con montos, comisión y neto. Del paciente solo va su seudónimo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label htmlFor="desde" className="text-slate-300 text-xs">Desde</Label>
              <Input
                id="desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label htmlFor="hasta" className="text-slate-300 text-xs">Hasta</Label>
              <Input
                id="hasta"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <Button onClick={exportar} className="bg-teal-600 hover:bg-teal-700 text-white">
              <Download className="w-4 h-4 mr-2" />
              Descargar CSV
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMetricas;
